import { useEffect, useState } from "react";

import { HttpStatusCode } from "axios";
import {
    FormControl,
    FormLabel,
    FormErrorMessage,
    Input,
    Divider,
    Button,
    useDisclosure,
    useToast,
    Container,
    Heading,
    Box,
    Flex,
    Checkbox,
    CheckboxGroup,
    HStack,
    Icon,
    Text,
    Center
} from "@chakra-ui/react";
import { Formik, Field, Form } from "formik";
import { useLocation } from "react-router-dom";
import { FaEye } from "react-icons/fa";
import { UserManager } from "oidc-client-ts";

import ArrowIcon from "@/assets/ArrowIcon";
import EditIcon from "@/assets/EditIcon";
import { COLORS, DURATION_TOAST } from "@/common/constants";
import Footer from "@/components/Layouts/Footer";
import Header from "@/components/Layouts/Header";
import ModalConfirmation from "@/components/Modals/ModalConfirmation";
import FileUploader from "@/components/Fields/FileUploader";
import SelectCountry from "@/components/Selects/SelectCountry";
import { appConfig } from "@/common/config";
import { registerUserSchema } from "@/schemas/registerUserSchema";
import { getInspectorById, postInspector, putInspector } from "@/services/inspectorService";
import { downloadFile } from "@/services/downloadService";
import { getShipOwnerById, postShipOwner, putShipOwner } from "@/services/shipOwnerService";

const getMyBg = (status: string) => {
    switch (status) {
        case '':
            break;
        case 'Waiting For Review':
            return '#0BA5EC';
        case 'In Review':
            return 'green';
        case 'Approved':
            break;
        case 'Rejected':
            return 'red';
        default:
            break;
    }
}

const DynamicBox = ({ registerStatus, dataDetail, getMyBg }: any) => {
    return (
        <Box
            bg={getMyBg(registerStatus)}
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            p={4}
            color="white"
            mb={dataDetail?.note && dataDetail.status === 4 ? 1 : 8}
            mt={dataDetail?.note && dataDetail.status === 4 ? 3 : 2}
            borderRadius={20}
        >
            <Heading
                as="h4"
                size="md"
                fontWeight="normal"
                whiteSpace="nowrap"
            >
                {registerStatus}
            </Heading>
        </Box>
    );
};

const RegisterUser = ({ handleSetIsAuthenticated }: any) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const location = useLocation();
    const toast = useToast();
    const userManager = new UserManager(appConfig.settings);

    const { role, appId } = location.state || {};

    const [confirmationText, setConfirmationTest] = useState("");
    const [confirmationTitleButton, setConfirmationTitleButton] = useState("");
    const [dataAdd, setDataAdd] = useState(null) as any;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [title, setTitle] = useState("");
    const [registerStatus, setRegisterStatus] = useState("");
    const [dataDetail, setDataDetail] = useState(null) as any;
    const [editableDocs, setEditableDocs] = useState<string[]>([]);

    const handleSubmit = async (data: any) => {
        setTitle("Register User?");
        setConfirmationTest("Are you sure you want to register?");
        setConfirmationTitleButton("Register User");
        onOpen();
        setDataAdd(data);
        setIsSubmitting(true);
    }

    const generateDataAddInspectingCompany = () => {
        const data = new FormData();
        data.append('InspectorId', appId ?? '');
        data.append('Sirebire', dataAdd.type.includes('SIRE | BIRE'));
        data.append('Vetting', dataAdd.type.includes('Vetting'));
        data.append('CompanyName', dataAdd.companyName ?? '');
        data.append('Address', dataAdd.address ?? '');
        data.append('Country', dataAdd.country ?? '');
        data.append('ContactPerson', dataAdd.contactPerson ?? '');
        data.append('Email', dataAdd.email ?? '');
        data.append('AlternateEmail', dataAdd.altEmail ?? '');
        data.append('Phone', dataAdd.phone ?? '');
        data.append('DGT1File', dataAdd.dgt1 ?? '');
        data.append('CORFile', dataAdd.cor ?? '');
        data.append('CompanyProfileFile', dataAdd.companyProfile ?? '');
        data.append('FinanceReportsFile', dataAdd.latestFinancialReport ?? '');
        data.append('BankDetailFile', dataAdd.bankDetails ?? '');
        data.append('NIBBusRegFile', dataAdd.NIB ?? '');
        data.append('RegisterStatus', '1');
        // for (let [key, value] of data.entries()) {
        //     console.log(`${key}: ${value}`);
        // }
        return data;
    }

    const generateDataAddShipOperator = () => {
        const data = new FormData();
        data.append('ShipOwnerId', appId ?? '');
        data.append('Sirebire', dataAdd.type.includes('SIRE | BIRE'));
        data.append('Vetting', dataAdd.type.includes('Vetting'));
        data.append('OnBehalfCompany', dataAdd.companyName ?? '');
        data.append('Address', dataAdd.address ?? '');
        data.append('Country', dataAdd.country ?? '');
        data.append('ContactPerson', dataAdd.contactPerson ?? '');
        data.append('Email', dataAdd.email ?? '');
        data.append('AlternateEmail', dataAdd.altEmail ?? '');
        data.append('Phone', dataAdd.phone ?? '');
        data.append('Documentfile', dataAdd.doc ?? '');
        data.append('RegisterStatus', '1');
        // for (let [key, value] of data.entries()) {
        //     console.log(`${key}: ${value}`);
        // }
        return data;
    }

    const handleConfirm = async () => {
        onClose();

        if (title === "Cancel to Register User?") {
            await userManager.clearStaleState();
            await userManager.signoutRedirect();
            return;
        }

        let response = { status: 0 };
        if (role === 'inspectingcompany') {
            const obj = generateDataAddInspectingCompany();
            if (dataDetail) {
                response = await putInspector(obj) as any;
            } else {
                response = await postInspector(obj) as any;
            }
        } else {
            const obj = generateDataAddShipOperator();
            if (dataDetail) {
                response = await putShipOwner(obj) as any;
            } else {
                response = await postShipOwner(obj) as any;
            }
        }
        setIsSubmitting(false);
        if (response.status === HttpStatusCode.Ok) {
            toast({
                title: 'User registered.',
                description: "We've registered user for you.",
                status: 'success',
                duration: DURATION_TOAST,
                isClosable: true,
            });

            setTimeout(async () => {
                await userManager.clearStaleState();
                await userManager.signoutRedirect();
            }, 1000);
        } else {
            toast({
                title: 'User failed to register.',
                description: "We unabled to register user for you.",
                status: 'error',
                duration: DURATION_TOAST,
                isClosable: true,
            });
        }
    }

    const handleConfirmToCancel = () => {
        setIsSubmitting(false);
        onClose();
    }

    // const handleDrop = (acceptedFiles: File[], setFieldValue: any, fieldName: string) => {
    //     setFieldValue(fieldName, acceptedFiles[0]);
    // };

    const handleDownload = async (id: string) => {
			await downloadFile(id);
    }

    const handleShowEditImage = (name: string) => {
        setEditableDocs((prevDocs) => {
            if (prevDocs.includes(name)) {
                return prevDocs.filter((doc) => doc !== name);
            }

            return [...prevDocs, name];
        });
    }

    const getDataDetailInspectingCompany = async () => {
        const response = await getInspectorById(appId) as any;
        if (response.status === HttpStatusCode.Ok) {
            setDataDetail(response.data);
            switch (response.data.status) {
                case 0:
                    setRegisterStatus('');
                    break;
                case 1:
                    setRegisterStatus('Waiting For Review');
                    break;
                case 2:
                    setRegisterStatus('In Review');
                    break;
                case 3:
                    setRegisterStatus('Approved');
                    break;
                case 4:
                    setRegisterStatus('Rejected');
                    break;
                default:
                    setRegisterStatus('');
                    break;
            }
        }
    }

    const getDataDetailShipOperator = async () => {
        const response = await getShipOwnerById(appId) as any;
        if (response.status === HttpStatusCode.Ok) {
            setDataDetail(response.data);
            switch (response.data.status) {
                case 0:
                    setRegisterStatus('');
                    break;
                case 1:
                    setRegisterStatus('Waiting For Review');
                    break;
                case 2:
                    setRegisterStatus('In Review');
                    break;
                case 3:
                    setRegisterStatus('Approved');
                    break;
                case 4:
                    setRegisterStatus('Rejected');
                    break;
                default:
                    setRegisterStatus('');
                    break;
            }
        }
    }

    useEffect(() => {
        if (appId) {
            if (role === 'inspectingcompany') {
                getDataDetailInspectingCompany();
            } else if (role === 'shipoperator') {
                getDataDetailShipOperator();
            }
        }
    }, [appId])

    return (
        <Flex height="100vh" width="100%">
            <Box
                display="flex"
                flexDirection="column"
                bgColor="#f8fafc"
                width="100%"
                overflowY="auto"
            >
                <Header handleSetIsAuthenticated={handleSetIsAuthenticated} isShowMenu={false} />
                <Container flex="1" py={4} maxWidth='none' justifyItems="center">
                    <div className="form-container" style={{ width: 'calc(100% - 20px)' }}>
                        <ModalConfirmation
                            isOpen={isOpen}
                            onClose={onClose}
                            title={title}
                            handleCancel={handleConfirmToCancel}
                            handleConfirm={handleConfirm}
                            confirmationText={confirmationText}
                            confirmButtonText={confirmationTitleButton}
                        />
                        <Heading textAlign="center" mb={[0, 1, 3].includes(dataDetail?.status) ? 5 : 5}>Register {role === 'shipoperator' ? 'Ship Operator' : 'Inspecting Company'}</Heading>
                        {['Waiting For Review', 'In Review', 'Rejected'].includes(registerStatus) &&
                            <Center>
                                <DynamicBox registerStatus={registerStatus} dataDetail={dataDetail} getMyBg={getMyBg} />
                            </Center>
                        }
                        {registerStatus === 'Rejected' &&
                            <Center>
                                <Text mb={10} mt={5} fontWeight='normal'><strong>Reason :</strong> {dataDetail?.note || ''}</Text>
                            </Center>
                        }
                        <Formik
                            initialValues={{
                                type: dataDetail ? [dataDetail?.sirebire ? 'SIRE | BIRE' : '', dataDetail?.vetting ? 'Vetting' : ''] : [],
                                companyName: role === 'inspectingcompany' ? dataDetail?.companyName : dataDetail?.onBehalfCompany,
                                address: dataDetail?.address,
                                country: dataDetail?.country,
                                contactPerson: dataDetail?.contactPerson,
                                email: dataDetail?.email,
                                altEmail: dataDetail?.alternateEmail,
                                phone: dataDetail?.phone,
                                firstName: dataDetail?.firstName,
                                lastName: dataDetail?.lastName,
                                doc: '',
                                dgt1: '',
                                cor: '',
                                companyProfile: '',
                                lapKeuAkhir: '',
                                bankDetails: '',
                                NIB: ''
                            }}
                            onSubmit={(values) => {
                                handleSubmit(values);
                            }}
                            validationSchema={() => registerUserSchema(role, dataDetail)}
                            enableReinitialize={true}
                        >
                            {(props) => (
                                <Form>
                                    {role === 'inspectingcompany' && <Field name='type'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.type && form.touched.type} marginBottom={4}>
                                                <div className="form-field-container">
                                                    <FormLabel htmlFor="type" className="form-label">Type<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <CheckboxGroup
                                                            value={props.values.type}
                                                            onChange={(value: string[]) => props.setFieldValue('type', value)}
                                                            isDisabled={dataDetail?.status === 2 || isSubmitting}
                                                        >
                                                            <Flex gap={8}>
                                                                <Checkbox value="SIRE | BIRE">SIRE | BIRE</Checkbox>
                                                                <Checkbox value="Vetting">Vetting</Checkbox>
                                                            </Flex>
                                                        </CheckboxGroup>
                                                        <FormErrorMessage>{form.errors.type}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>}
                                    <Field name='companyName'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.companyName && form.touched.companyName} marginBottom={4}>
                                                <div className="form-field-container">
                                                    <FormLabel htmlFor="companyName" className="form-label">Company Name<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} id="companyName" placeholder='Input Company Name' className="form-input" isDisabled={dataDetail?.status === 2 || isSubmitting} />
                                                        <FormErrorMessage>{form.errors.companyName}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='address'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.address && form.touched.address} marginBottom={4}>
                                                <div className="form-field-container">
                                                    <FormLabel htmlFor="address" className="form-label">Address<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} id="address" placeholder='Input Address' className="form-input" isDisabled={dataDetail?.status === 2 || isSubmitting} />
                                                        <FormErrorMessage>{form.errors.address}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='country'>
                                        {({ form }: any) => (
                                            <FormControl isInvalid={form.errors.country && form.touched.country} marginBottom={4}>
                                                <div className="form-field-container">
                                                    <FormLabel htmlFor="country" className="form-label">Country<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <SelectCountry name='country' handleChange={({ value }: any) => props.setFieldValue('country', value)} value={props.values.country} hasError={form.errors.country && form.touched.country} defaultValue={props.values.country} isDisabled={dataDetail?.status === 2 || isSubmitting} />
                                                        <FormErrorMessage>{form.errors.country}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='contactPerson'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.contactPerson && form.touched.contactPerson} marginBottom={4}>
                                                <div className="form-field-container">
                                                    <FormLabel htmlFor="contactPerson" className="form-label">Contact Person<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} id="contactPerson" placeholder='Input Contact Person' className="form-input" isDisabled={dataDetail?.status === 2 || isSubmitting} />
                                                        <FormErrorMessage>{form.errors.contactPerson}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='email'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.email && form.touched.email} marginBottom={4}>
                                                <div className="form-field-container">
                                                    <FormLabel htmlFor="email" className="form-label">Email<span style={{ color: 'red' }}>*</span></FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} id="email" placeholder='Input Email' className="form-input" isDisabled={dataDetail?.status === 2 || isSubmitting} />
                                                        <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='altEmail'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.altEmail && form.touched.altEmail} marginBottom={4}>
                                                <div className="form-field-container">
                                                    <FormLabel htmlFor="altEmail" className="form-label">Alternative Email</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} id="altEmail" placeholder='Input Alternative Email' className="form-input" isDisabled={dataDetail?.status === 2 || isSubmitting} />
                                                        <FormErrorMessage>{form.errors.altEmail}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    <Field name='phone'>
                                        {({ field, form }: any) => (
                                            <FormControl isInvalid={form.errors.phone && form.touched.phone} style={{ marginBottom: 15 }}>
                                                <div className="form-field-container">
                                                    <FormLabel htmlFor="altEmail" className="form-label">Phone Number</FormLabel>
                                                    <div className="form-input-container">
                                                        <Input {...field} placeholder='Phone Number' className="form-input" isDisabled={dataDetail?.status === 2 || isSubmitting} />
                                                        <FormErrorMessage>{form.errors.phone}</FormErrorMessage>
                                                    </div>
                                                </div>
                                            </FormControl>
                                        )}
                                    </Field>
                                    {role === 'shipoperator' &&
                                        <Field name='doc'>
                                            {({ form }: any) => (
                                                <FormControl isInvalid={form.errors.doc && form.submitCount} style={{ marginBottom: 15 }}>
                                                    <div className="form-field-container">
                                                        <FormLabel className="form-label">DOC</FormLabel>
                                                        <div className="form-input-container">
                                                            {dataDetail?.documents && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                <Box
                                                                    borderWidth="1px"
                                                                    borderRadius="md"
                                                                    padding={4}
                                                                    backgroundColor="green.50"
                                                                    borderColor="green.200"
                                                                    width="100%"
                                                                    marginLeft={1}
                                                                    marginBottom="15px"
                                                                >
                                                                    <HStack
                                                                        borderWidth="1px"
                                                                        borderRadius="md"
                                                                        padding={3}
                                                                        backgroundColor="white"
                                                                        justifyContent="space-between"
                                                                        width="100%"
                                                                    >
                                                                        <HStack>
                                                                            <Text>DOC File</Text>
                                                                        </HStack>
                                                                        <HStack cursor='pointer' onClick={() => handleDownload(dataDetail.documents)}>
                                                                            <Icon as={FaEye} />
                                                                            <Text>Download Document</Text>
                                                                        </HStack>
                                                                    </HStack>
                                                                </Box>
                                                                {(registerStatus !== 'In Review' && !isSubmitting) && <EditIcon
                                                                    style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                                    onClick={() => handleShowEditImage('doc')}
                                                                />}
                                                            </div>}

                                                            {(!dataDetail?.documents || editableDocs.includes('doc')) &&
                                                                <div style={{ width: '95%' }}>
                                                                    <FileUploader
                                                                        onDrop={(e) => props.setFieldValue('doc', e[0])}//handleDrop(e, props.setFieldValue, 'doc')}
                                                                        isInvalid={form.errors.doc && form.submitCount}
                                                                        type="registration-shipoperator"
                                                                        isDisabled={props.isSubmitting}
                                                                        isPadding={false}
                                                                    />
                                                                </div>
                                                            }
                                                            <FormErrorMessage>{form.errors.doc}</FormErrorMessage>
                                                        </div>
                                                    </div>
                                                </FormControl>
                                            )}
                                        </Field>
                                    }
                                    {role === 'inspectingcompany' &&
                                        <>
                                            <Field name='dgt1'>
                                                {({ form }: any) => (
                                                    <FormControl isInvalid={form.errors.dgt1 && form.submitCount} style={{ marginBottom: 15 }}>
                                                        <div className="form-field-container">
                                                            <FormLabel className="form-label">DGT-1<span style={{ color: 'red' }}>*</span></FormLabel>
                                                            <div className="form-input-container">
                                                                {dataDetail?.dgT1 && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Box
                                                                        borderWidth="1px"
                                                                        borderRadius="md"
                                                                        padding={4}
                                                                        backgroundColor="green.50"
                                                                        borderColor="green.200"
                                                                        width="100%"
                                                                        marginLeft={1}
                                                                        marginBottom="15px"
                                                                    >
                                                                        <HStack
                                                                            borderWidth="1px"
                                                                            borderRadius="md"
                                                                            padding={3}
                                                                            backgroundColor="white"
                                                                            justifyContent="space-between"
                                                                            width="100%"
                                                                        >
                                                                            <HStack>
                                                                                <Text>DGT-1 File</Text>
                                                                            </HStack>
                                                                            <HStack cursor='pointer' onClick={() => handleDownload(dataDetail.dgT1)}>
                                                                                <Icon as={FaEye} />
                                                                                <Text>Download Document</Text>
                                                                            </HStack>
                                                                        </HStack>
                                                                    </Box>
                                                                    {(registerStatus !== 'In Review' && !isSubmitting) && <EditIcon
                                                                        style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                                        onClick={() => handleShowEditImage('dgt1')}
                                                                    />}
                                                                </div>}

                                                                {(!dataDetail?.dgT1 || editableDocs.includes('dgt1')) &&
                                                                    <div style={{ width: '95%' }}>
                                                                        <FileUploader
                                                                            onDrop={(e) => props.setFieldValue('dgt1', e[0])} //handleDrop(e, props.setFieldValue, 'dgt1')}
                                                                            isInvalid={form.errors.dgt1 && form.submitCount}
                                                                            type="registration-inspectingcompany"
                                                                            isDisabled={props.isSubmitting}
                                                                            isPadding={false}
                                                                        />
                                                                    </div>
                                                                }
                                                                <FormErrorMessage>{form.errors.dgt1}</FormErrorMessage>
                                                            </div>
                                                        </div>
                                                    </FormControl>
                                                )}
                                            </Field>
                                            <Field name='cor'>
                                                {({ form }: any) => (
                                                    <FormControl isInvalid={form.errors.cor && form.submitCount} style={{ marginBottom: 15 }}>
                                                        <div className="form-field-container">
                                                            <FormLabel className="form-label">COR<span style={{ color: 'red' }}>*</span></FormLabel>
                                                            <div className="form-input-container">
                                                                {dataDetail?.cor && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Box
                                                                        borderWidth="1px"
                                                                        borderRadius="md"
                                                                        padding={4}
                                                                        backgroundColor="green.50"
                                                                        borderColor="green.200"
                                                                        width="100%"
                                                                        marginLeft={1}
                                                                        marginBottom="15px"
                                                                    >
                                                                        <HStack
                                                                            borderWidth="1px"
                                                                            borderRadius="md"
                                                                            padding={3}
                                                                            backgroundColor="white"
                                                                            justifyContent="space-between"
                                                                            width="100%"
                                                                        >
                                                                            <HStack>
                                                                                <Text>COR File</Text>
                                                                            </HStack>
                                                                            <HStack cursor='pointer' onClick={() => handleDownload(dataDetail.cor)}>
                                                                                <Icon as={FaEye} />
                                                                                <Text>Download Document</Text>
                                                                            </HStack>
                                                                        </HStack>
                                                                    </Box>
                                                                    {(registerStatus !== 'In Review' && !isSubmitting) && <EditIcon
                                                                        style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                                        onClick={() => handleShowEditImage('cor')}
                                                                    />}
                                                                </div>}

                                                                {(!dataDetail?.cor || editableDocs.includes('cor')) &&
                                                                    <div style={{ width: '95%' }}>
                                                                        <FileUploader
                                                                            onDrop={(e) => props.setFieldValue('cor', e[0])}//handleDrop(e, props.setFieldValue, 'cor')}
                                                                            isInvalid={form.errors.cor && form.submitCount}
                                                                            type="registration-inspectingcompany"
                                                                            isDisabled={props.isSubmitting}
                                                                            isPadding={false}
                                                                        />
                                                                    </div>
                                                                }
                                                                <FormErrorMessage>{form.errors.cor}</FormErrorMessage>
                                                            </div>
                                                        </div>
                                                    </FormControl>
                                                )}
                                            </Field>
                                            <Field name='companyProfile'>
                                                {({ form }: any) => (
                                                    <FormControl isInvalid={form.errors.companyProfile && form.submitCount} style={{ marginBottom: 15 }}>
                                                        <div className="form-field-container">
                                                            <FormLabel className="form-label">Company Profile</FormLabel>
                                                            <div className="form-input-container">
                                                                {dataDetail?.companyProfile && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Box
                                                                        borderWidth="1px"
                                                                        borderRadius="md"
                                                                        padding={4}
                                                                        backgroundColor="green.50"
                                                                        borderColor="green.200"
                                                                        width="100%"
                                                                        marginLeft={1}
                                                                        marginBottom="15px"
                                                                    >
                                                                        <HStack
                                                                            borderWidth="1px"
                                                                            borderRadius="md"
                                                                            padding={3}
                                                                            backgroundColor="white"
                                                                            justifyContent="space-between"
                                                                            width="100%"
                                                                        >
                                                                            <HStack>
                                                                                <Text>Company Profile File</Text>
                                                                            </HStack>
                                                                            <HStack cursor='pointer' onClick={() => handleDownload(dataDetail.companyProfile)}>
                                                                                <Icon as={FaEye} />
                                                                                <Text>Download Document</Text>
                                                                            </HStack>
                                                                        </HStack>
                                                                    </Box>
                                                                    {(registerStatus !== 'In Review' && !isSubmitting) && <EditIcon
                                                                        style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                                        onClick={() => handleShowEditImage('companyProfile')}
                                                                    />}
                                                                </div>}

                                                                {(!dataDetail?.companyProfile || editableDocs.includes('companyProfile')) &&
                                                                    <div style={{ width: '95%' }}>
                                                                        <FileUploader
                                                                            onDrop={(e) => props.setFieldValue('companyProfile', e[0])} //handleDrop(e, props.setFieldValue, 'companyProfile')}
                                                                            isInvalid={form.errors.companyProfile && form.submitCount}
                                                                            type="registration-inspectingcompany"
                                                                            isDisabled={props.isSubmitting}
                                                                            isPadding={false}
                                                                        />
                                                                    </div>
                                                                }
                                                                <FormErrorMessage>{form.errors.companyProfile}</FormErrorMessage>
                                                            </div>
                                                        </div>
                                                        
                                                    </FormControl>
                                                )}
                                            </Field>
                                            <Field name='latestFinancialReport'>
                                                {({ form }: any) => (
                                                    <FormControl isInvalid={form.errors.latestFinancialReport && form.submitCount} style={{ marginBottom: 15 }}>
                                                        <div className="form-field-container">
                                                            <FormLabel className="form-label">Latest Financial Report</FormLabel>
                                                            <div className="form-input-container">
                                                                {dataDetail?.financeReports && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Box
                                                                        borderWidth="1px"
                                                                        borderRadius="md"
                                                                        padding={4}
                                                                        backgroundColor="green.50"
                                                                        borderColor="green.200"
                                                                        width="100%"
                                                                        marginLeft={1}
                                                                        marginBottom="15px"
                                                                    >
                                                                        <HStack
                                                                            borderWidth="1px"
                                                                            borderRadius="md"
                                                                            padding={3}
                                                                            backgroundColor="white"
                                                                            justifyContent="space-between"
                                                                            width="100%"
                                                                        >
                                                                            <HStack>
                                                                                <Text>Latest Financial Report File</Text>
                                                                            </HStack>
                                                                            <HStack cursor='pointer' onClick={() => handleDownload(dataDetail.financeReports)}>
                                                                                <Icon as={FaEye} />
                                                                                <Text>Download Document</Text>
                                                                            </HStack>
                                                                        </HStack>
                                                                    </Box>
                                                                    {(registerStatus !== 'In Review' && !isSubmitting) && <EditIcon
                                                                        style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                                        onClick={() => handleShowEditImage('latestFinancialReport')}
                                                                    />}
                                                                </div>}

                                                                {(!dataDetail?.financeReports || editableDocs.includes('latestFinancialReport')) &&
                                                                    <div style={{ width: '95%' }}>
                                                                        <FileUploader
                                                                            onDrop={(e) => props.setFieldValue('latestFinancialReport', e[0])}//handleDrop(e, props.setFieldValue, 'latestFinancialReport')}
                                                                            isInvalid={form.errors.latestFinancialReport && form.submitCount}
                                                                            type="registration-inspectingcompany"
                                                                            isDisabled={props.isSubmitting}
                                                                            isPadding={false}
                                                                        />
                                                                    </div>
                                                                }
                                                                <FormErrorMessage>{form.errors.latestFinancialReport}</FormErrorMessage>
                                                            </div>
                                                        </div>
                                                    </FormControl>
                                                )}
                                            </Field>
                                            <Field name='bankDetails'>
                                                {({ form }: any) => (
                                                    <FormControl isInvalid={form.errors.bankDetails && form.submitCount} style={{ marginBottom: 15 }}>
                                                        <div className="form-field-container">
                                                            <FormLabel className="form-label">Bank Details</FormLabel>
                                                            <div className="form-input-container">
                                                                {dataDetail?.bankDetail && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Box
                                                                        borderWidth="1px"
                                                                        borderRadius="md"
                                                                        padding={4}
                                                                        backgroundColor="green.50"
                                                                        borderColor="green.200"
                                                                        width="100%"
                                                                        marginLeft={1}
                                                                        marginBottom="15px"
                                                                    >
                                                                        <HStack
                                                                            borderWidth="1px"
                                                                            borderRadius="md"
                                                                            padding={3}
                                                                            backgroundColor="white"
                                                                            justifyContent="space-between"
                                                                            width="100%"
                                                                        >
                                                                            <HStack>
                                                                                <Text>Bank Details File</Text>
                                                                            </HStack>
                                                                            <HStack cursor='pointer' onClick={() => handleDownload(dataDetail.bankDetail)}>
                                                                                <Icon as={FaEye} />
                                                                                <Text>Download Document</Text>
                                                                            </HStack>
                                                                        </HStack>
                                                                    </Box>
                                                                    {(registerStatus !== 'In Review' && !isSubmitting) && <EditIcon
                                                                        style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                                        onClick={() => handleShowEditImage('bankDetails')}
                                                                    />}
                                                                </div>}

                                                                {(!dataDetail?.bankDetail || editableDocs.includes('bankDetails')) &&
                                                                    <div style={{ width: '95%' }}>
                                                                        <FileUploader
                                                                            onDrop={(e) => props.setFieldValue('bankDetails', e[0])} //handleDrop(e, props.setFieldValue, 'bankDetails')}
                                                                            isInvalid={form.errors.bankDetails && form.submitCount}
                                                                            type="registration-inspectingcompany"
                                                                            isDisabled={props.isSubmitting}
                                                                            isPadding={false}
                                                                        />
                                                                    </div>
                                                                }
                                                                <FormErrorMessage>{form.errors.bankDetails}</FormErrorMessage>
                                                            </div>
                                                        </div>
                                                        
                                                    </FormControl>
                                                )}
                                            </Field>
                                            <Field name='NIB'>
                                                {({ form }: any) => (
                                                    <FormControl isInvalid={form.errors.NIB && form.submitCount} style={{ marginBottom: 15 }}>
                                                        <div className="form-field-container">
                                                            <FormLabel className="form-label">NIB / Company Bussiness Registration</FormLabel>
                                                            <div className="form-input-container">
                                                                {dataDetail?.nibBusReg && <div style={{ display: 'flex', alignItems: 'center' }}>
                                                                    <Box
                                                                        borderWidth="1px"
                                                                        borderRadius="md"
                                                                        padding={4}
                                                                        backgroundColor="green.50"
                                                                        borderColor="green.200"
                                                                        width="100%"
                                                                        marginLeft={1}
                                                                        marginBottom="15px"
                                                                    >
                                                                        <HStack
                                                                            borderWidth="1px"
                                                                            borderRadius="md"
                                                                            padding={3}
                                                                            backgroundColor="white"
                                                                            justifyContent="space-between"
                                                                            width="100%"
                                                                        >
                                                                            <HStack>
                                                                                <Text>NIB File</Text>
                                                                            </HStack>
                                                                            <HStack cursor='pointer' onClick={() => handleDownload(dataDetail.nibBusReg)}>
                                                                                <Icon as={FaEye} />
                                                                                <Text>Download Document</Text>
                                                                            </HStack>
                                                                        </HStack>
                                                                    </Box>
                                                                    {(registerStatus !== 'In Review' && !isSubmitting) && <EditIcon
                                                                        style={{ marginLeft: '10px', marginTop: '-20px', cursor: 'pointer' }}
                                                                        onClick={() => handleShowEditImage('NIB')}
                                                                    />}
                                                                </div>}

                                                                {(!dataDetail?.nibBusReg || editableDocs.includes('NIB')) &&
                                                                    <div style={{ width: '95%' }}>
                                                                        <FileUploader
                                                                            onDrop={(e) => props.setFieldValue('NIB', e[0])} //handleDrop(e, props.setFieldValue, 'NIB')}
                                                                            isInvalid={form.errors.NIB && form.submitCount}
                                                                            type="registration-inspectingcompany"
                                                                            isDisabled={props.isSubmitting || registerStatus === 'In Review'}
                                                                            isPadding={false}
                                                                        />
                                                                    </div>
                                                                }
                                                                <FormErrorMessage>{form.errors.NIB}</FormErrorMessage>
                                                            </div>
                                                        </div>
                                                        
                                                    </FormControl>
                                                )}
                                            </Field>
                                        </>
                                    }

                                    <Divider />
                                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                        <Button
                                            mt={4}
                                            backgroundColor={COLORS.BUTTON.BACKGROUND}
                                            color={COLORS.BUTTON.FOREGROUND}
                                            isLoading={isSubmitting}
                                            type='submit'
                                            rightIcon={<ArrowIcon />}
                                            isDisabled={registerStatus === 'In Review'}
                                        >
                                            Register
                                        </Button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </div>
                </Container>
                <Footer />
            </Box>
        </Flex>
    )
}

export default RegisterUser;
